import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { fetchLicenseStatus, LicenseStatus } from '@/api/license';
import { env } from '@/utils/constants/env';

type CheckStatus = 'idle' | 'loading' | 'success' | 'error';

interface LicenseStore {
  checkStatus: CheckStatus;
  licenseData: LicenseStatus | null;
  errorMessage: string | null;
  checkLicense: () => Promise<void>;
  isBlocked: () => boolean;
  isGracePeriod: () => boolean;
  getMaxWorkspaces: () => number;
}

const useLicenseState = create<LicenseStore>()(
  persist(
    (set, get) => ({
      checkStatus: 'idle',
      licenseData: null,
      errorMessage: null,

      checkLicense: async () => {
        set({ checkStatus: 'loading', errorMessage: null });
        try {
          const data = await fetchLicenseStatus(`${env.apiTasklistBaseUrl}/api/v1/license-service/status`);
          set({ checkStatus: 'success', licenseData: data });
        } catch (error) {
          set({
            checkStatus: 'error',
            errorMessage:
              error instanceof Error ? error.message : 'Unable to reach license service',
          });
        }
      },

      isBlocked: () => {
        if (!env.licenseCheckEnabled) return false;
        const { checkStatus, licenseData } = get();
        if (checkStatus === 'error') return true;
        if (!licenseData) return true;
        return licenseData.is_blocked === true || licenseData.ready?.is_blocked === true;
      },

      isGracePeriod: () => {
        if (!env.licenseCheckEnabled) return false;
        const { licenseData } = get();
        if (!licenseData || licenseData.is_blocked) return false;
        const expiresAt = licenseData.ready?.subscription?.expires_at;
        if (!expiresAt) return false;
        return new Date(expiresAt) < new Date();
      },

      getMaxWorkspaces: () => {
        if (!env.licenseCheckEnabled) return 0;
        const { licenseData } = get();
        return licenseData?.ready?.plan?.max_workspaces ?? 0;
      },
    }),
    {
      name: 'license-storage',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({ licenseData: state.licenseData }),
    }
  )
);

export default useLicenseState;
